'use client';

import { useEffect, useState } from 'react';

import { useTheme } from '@mui/material/styles';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Room } from '@mui/icons-material';
import { TableCell, TableRow, Typography } from '@mui/material';
import { EventItem, useMatchsPlannerContext } from '@scorenco/components';
import { Types } from '@scorenco/core';
import { parseISO } from 'date-fns';
import { useRouter } from 'next/router';

import { GetEventPlayingTeamInfosDocument } from './MatchRowForm.query.generated';
import type { GetEventPlayingTeamInfosQuery } from './MatchRowForm.query.generated';
import { createKeywords } from './utils';

type MatchRowFormProps = {
  event?: Types.Competitions_Event_Detail;
  searched?: string;
  club?: Types.Clubs_Club;
  convocationSettings?: {
    __typename: 'widgets_convocation_settings';
    exclude_task_ids?: number[];
  };
};

type EventTeam = {
  is_team_of_club?: number;
};

const getEventTeams = (value: unknown): EventTeam[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === 'object' && item !== null
    )
    .map((team) => ({
      is_team_of_club:
        typeof team.is_team_of_club === 'number'
          ? team.is_team_of_club
          : undefined,
    }));
};

const getStringFromUnknown = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

export const MatchRowForm = ({
  event,
  searched,
  club,
  convocationSettings,
}: MatchRowFormProps) => {
  const router = useRouter();
  const theme = useTheme();
  const { clubId, isLoading, tasks } = useMatchsPlannerContext();

  const eventTeams = getEventTeams(event?.teams);
  const isHome = eventTeams[0]?.is_team_of_club === 1;
  const teamInfoId = event?.playing_teams?.[0]?.info?.id;
  const showLicence = club?.planner_settings?.show_licence;

  const eventInfoQuery = useQuery(GetEventPlayingTeamInfosDocument, {
    variables: {
      teamInfoId: teamInfoId as number,
      isHome,
      showLicence: showLicence ?? false,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !teamInfoId || !club,
    context: {
      role: 'anonymous',
    },
  });
  const teamInfo = eventInfoQuery.data?.competitions_playing_team_info_by_pk;
  const meetingTime = getStringFromUnknown(teamInfo?.meeting_time);

  const [keywords, setKeywords] = useState<string[] | undefined>([]);

  useEffect(() => {
    createKeywords(
      event,
      teamInfo as Types.Competitions_Playing_Team_Info | undefined,
      setKeywords
    );
  }, [teamInfo]);

  const placeName = (isHome && teamInfo?.equipment?.name) || event?.place?.name;

  if (
    searched &&
    !keywords?.some((keyword) =>
      keyword
        ?.toLocaleLowerCase('fr')
        ?.normalize('NFD')
        ?.replace(/[\u0300-\u036f]/g, '')
        ?.includes(
          searched
            ?.toLocaleLowerCase('fr')
            ?.normalize('NFD')
            ?.replace(/[\u0300-\u036f]/g, '')
        )
    )
  ) {
    return null;
  }

  return (
    <TableRow
      css={css`
        border-bottom: 0.75px solid ${theme.palette.divider};
      `}
    >
      <TableCell
        css={() => css`
          min-width: 30rem;
          max-width: 30rem;
          ${!teamInfo?.status && 'filter: grayscale(100%);'};
        `}
        align="center"
      >
        <EventItem
          event={event}
          clubId={clubId}
          showShortName={true}
          showDate={true}
        />
        {placeName && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontStyle: 'italic',
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Room sx={{ fontSize: '1rem' }} />
            {placeName}
          </Typography>
        )}
      </TableCell>
      {teamInfo?.status ? (
        <>
          <TableCell>
            {meetingTime
              ? parseISO(meetingTime).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'Heure non-renseignée'}
          </TableCell>

          {tasks
            ?.filter(
              (task) =>
                !convocationSettings?.exclude_task_ids?.includes(task.id)
            )
            .map((task) => (
              <TaskCell
                key={task.id}
                task={task}
                isHome={isHome}
                teamInfo={teamInfo as TeamInfo}
                club={club}
              />
            ))}
          <TableCell>
            <Typography variant="body2">{teamInfo?.public_comment}</Typography>
          </TableCell>
        </>
      ) : (
        !isLoading && (
          <td
            style={{
              paddingLeft: '1rem',
              textAlign: 'center',
            }}
            colSpan={5}
          >
            'Informations de matchs non disponibles pour le moment. 😢
          </td>
        )
      )}
    </TableRow>
  );
};

type TeamInfo = NonNullable<
  GetEventPlayingTeamInfosQuery['competitions_playing_team_info_by_pk']
>;

const TaskCell = ({
  task,
  isHome,
  teamInfo,
  club,
}: {
  task: Types.Tasks_Task;
  isHome: boolean;
  teamInfo: TeamInfo;
  club?: Types.Clubs_Club;
}) => {
  const theme = useTheme();
  const isTask =
    task.game_type === '' ||
    (task.game_type === 'home' && isHome) ||
    (task.game_type === 'away' && !isHome);

  const [isOfficial] = useState(
    teamInfo?.playing_team?.team_in_season?.team?.team_tasks?.find(
      (job) => job.task_id === task.id
    )?.officials &&
      teamInfo?.jobs?.find((t) => t.task?.id === task.id)?.volunteers
        ?.length === 0
  );

  return (
    <TableCell
      css={css`
        background: ${!isTask && 'rgba(224, 224, 224, 0.3)'};
        border-right: 0.75px solid ${theme.palette.divider};
        border-left: 0.75px solid ${theme.palette.divider};
      `}
    >
      {isTask && isOfficial ? (
        <Typography>👨‍💼 Type de convocation officielle</Typography>
      ) : (
        <ul style={{ margin: 0, padding: 0 }}>
          {teamInfo?.jobs
            ?.find((t) => t.task?.id === task.id)
            ?.volunteers?.map((volunteer) => {
              return (
                <li
                  key={volunteer.id}
                  style={{ listStyle: 'none', marginBottom: '0.6rem' }}
                >
                  {volunteer?.member?.first_name}{' '}
                  {volunteer?.member?.last_name?.toUpperCase()}
                  {club?.planner_settings?.show_licence &&
                    !!volunteer?.member?.licence_details?.[0]?.number?.length &&
                    ' - ' + volunteer?.member?.licence_details?.[0]?.number}
                </li>
              );
            })}
        </ul>
      )}
    </TableCell>
  );
};
